import { memo, MouseEvent, useCallback, useMemo } from 'react';
import { VisibilityOffOutlined } from '@mui/icons-material';
import { Box, Switch, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateBalanceSheet_FieldsFragment,
  EstateBalanceSheetAssetColumns,
  EstateBalanceSheetLiabilityColumns,
} from 'queries';

import EstateBalanceSheetDropdownMenu from './estate-balance-sheet-dropdown-menu';

const useStyles = makeStyles(() => ({
  container: {
    width: '300px',
    display: 'flex',
    alignItems: 'center',
  },
}));

const assetsMenuItems: AssetsMenuItem[] = [
  { label: 'Allocate To', columnName: EstateBalanceSheetAssetColumns.AllocateTo },
  { label: 'Asset Description', columnName: EstateBalanceSheetAssetColumns.AssetDescription },
  { label: 'Asset Type', columnName: EstateBalanceSheetAssetColumns.AssetType },
  { label: 'Category', columnName: EstateBalanceSheetAssetColumns.Category },
  { label: 'Comment / Notes', columnName: EstateBalanceSheetAssetColumns.Notes },
  { label: 'Post Death Appreciation', columnName: EstateBalanceSheetAssetColumns.CurrentValue },
  { label: 'Current Value Date', columnName: EstateBalanceSheetAssetColumns.CurrentValueDate },
  { label: 'Distribution Type', columnName: EstateBalanceSheetAssetColumns.DistributionType },
  {
    label: 'How was this held during lifetime?',
    columnName: EstateBalanceSheetAssetColumns.LifetimeHolding,
  },
  {
    label: 'Include in taxable estate?',
    columnName: EstateBalanceSheetAssetColumns.CanIncludeInTaxableEstate,
  },
  { label: 'Value', columnName: EstateBalanceSheetAssetColumns.InitialValue },
  { label: 'Initial Value Date/DOD', columnName: EstateBalanceSheetAssetColumns.InitialValueDate },
  {
    label: 'Name of Beneficiary(ies) if Non-Probate',
    columnName: EstateBalanceSheetAssetColumns.NonProbateBeneficiaryName,
  },
  { label: 'Source', columnName: EstateBalanceSheetAssetColumns.Source },
];

const liabilitiesMenuItems: LiabilitiesMenuItem[] = [
  {
    label: 'Account # / Invoice #',
    columnName: EstateBalanceSheetLiabilityColumns.AccountInvoiceNumber,
  },
  { label: 'Allocate To', columnName: EstateBalanceSheetLiabilityColumns.AllocateTo },
  { label: 'Balance', columnName: EstateBalanceSheetLiabilityColumns.Balance },
  { label: 'Category', columnName: EstateBalanceSheetLiabilityColumns.Category },

  { label: 'Comment / Notes', columnName: EstateBalanceSheetLiabilityColumns.Notes },
  {
    label: 'Deductible expense?',
    columnName: EstateBalanceSheetLiabilityColumns.IsDeductibleExpense,
  },
  { label: 'Due Date', columnName: EstateBalanceSheetLiabilityColumns.DueDate },
  { label: 'Interest Rate', columnName: EstateBalanceSheetLiabilityColumns.InterestRate },
  {
    label: 'Liability Description (Company)',
    columnName: EstateBalanceSheetLiabilityColumns.LiabilityDescription,
  },
  { label: 'Name', columnName: EstateBalanceSheetLiabilityColumns.LiabilityName },
  { label: 'Paid By, if Paid', columnName: EstateBalanceSheetLiabilityColumns.PaidBy },
  { label: 'Source', columnName: EstateBalanceSheetLiabilityColumns.Source },
  { label: 'Status', columnName: EstateBalanceSheetLiabilityColumns.Status },
];

type Props = {
  estateBalanceSheet: EstateBalanceSheet_FieldsFragment;
  onToggleLiabilitiesColumnVisibility: (columnName: EstateBalanceSheetLiabilityColumns) => void;
  onToggleAssetsColumnVisibility: (columnName: EstateBalanceSheetAssetColumns) => void;
};

type AssetsMenuItem = {
  label: string;
  columnName: EstateBalanceSheetAssetColumns;
};

type LiabilitiesMenuItem = {
  label: string;
  columnName: EstateBalanceSheetLiabilityColumns;
};

const MenuItemOption = memo(
  ({
    columnName,
    label,
    onToggleVisibility,
    isColumnHidden,
  }: {
    columnName: EstateBalanceSheetAssetColumns | EstateBalanceSheetLiabilityColumns;
    label: string;
    onToggleVisibility?: () => void;
    isColumnHidden: boolean;
  }) => {
    const classes = useStyles();

    const handleClick = useCallback(
      (evt: MouseEvent) => {
        evt.stopPropagation();
        if (onToggleVisibility) {
          onToggleVisibility();
        }
      },
      [onToggleVisibility],
    );

    return (
      <Box className={classes.container} key={columnName} onClick={handleClick}>
        {onToggleVisibility && <Switch checked={!isColumnHidden} size="small" color="success" />}
        <Typography ml={2}>{label}</Typography>
      </Box>
    );
  },
);

const EstateBalanceSheetColumnVisibilityPanel = ({
  estateBalanceSheet,
  onToggleLiabilitiesColumnVisibility,
  onToggleAssetsColumnVisibility,
}: Props) => {
  const classes = useStyles();

  const createLiabilitiesColumnMenuItem = useCallback(
    (option: LiabilitiesMenuItem) => {
      let isColumnHidden = estateBalanceSheet.hiddenLiabilityColumns.includes(option.columnName);

      return (
        <MenuItemOption
          key={option.columnName}
          columnName={option.columnName}
          label={option.label}
          onToggleVisibility={() => onToggleLiabilitiesColumnVisibility(option.columnName)}
          isColumnHidden={isColumnHidden}
        />
      );
    },
    [estateBalanceSheet, onToggleLiabilitiesColumnVisibility],
  );

  const createAssetColumnMenuItem = useCallback(
    (option: AssetsMenuItem) => {
      let isColumnHidden = estateBalanceSheet.hiddenAssetColumns.includes(option.columnName);

      return (
        <MenuItemOption
          key={option.columnName}
          columnName={option.columnName}
          label={option.label}
          onToggleVisibility={() => onToggleAssetsColumnVisibility(option.columnName)}
          isColumnHidden={isColumnHidden}
        />
      );
    },
    [estateBalanceSheet, onToggleAssetsColumnVisibility],
  );

  const createGroupTitle = useCallback(
    (label: string) => {
      const handleClick = (evt: MouseEvent) => {
        evt.stopPropagation();
      };

      return (
        <Box className={classes.container} key={label} onClick={handleClick}>
          <Typography ml={2}>{label}</Typography>
        </Box>
      );
    },
    [classes.container],
  );

  const menuItems = useMemo(() => {
    return [
      {
        content: createGroupTitle('Assets'),
      },
      ...assetsMenuItems.map((option) => ({
        content: createAssetColumnMenuItem(option),
      })),
      {
        content: createGroupTitle('Liabilities'),
      },
      ...liabilitiesMenuItems.map((option) => ({
        content: createLiabilitiesColumnMenuItem(option),
      })),
    ];
  }, [createAssetColumnMenuItem, createGroupTitle, createLiabilitiesColumnMenuItem]);

  return (
    <EstateBalanceSheetDropdownMenu
      title="Customize Columns"
      startIcon={<VisibilityOffOutlined />}
      menuItems={menuItems}
    />
  );
};

export default EstateBalanceSheetColumnVisibilityPanel;
